import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  InputLabel,
  CircularProgress,
  styled,
  Grid,
  ImageList,
  ImageListItem,
} from "@mui/material";
import myIcon from "../assets/svg/logo.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from "react-router-dom";
import { signIn, getCurrentUser, fetchUserAttributes } from "@aws-amplify/auth";
import { getAllWebsites } from "../api/websiteService";
import axios from "axios";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import { validateLicense } from "../api/licensing";
import { useDispatch } from "react-redux";
import { setLicenseData } from "../store/licenseSlice";
import { handleLogout } from "../services/authService";

// Import your PNG images
import img1 from "../assets/images/img1.png";
import img2 from "../assets/images/img2.png";
import img3 from "../assets/images/img3.png";
import img4 from "../assets/images/img4.png";
import img5 from "../assets/images/img5.png";
import img6 from "../assets/images/img6.png";
import img7 from "../assets/images/img7.png";
import img8 from "../assets/images/img8.png";
import img9 from "../assets/images/img9.png";
import img10 from "../assets/images/img10.png";
import img11 from "../assets/images/img11.png";
import img12 from "../assets/images/img12.png";
import img13 from "../assets/images/img13.png";

const LoginPage = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isForgotPasswordDisabled, setIsForgotPasswordDisabled] =
    useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const imageItems = [
    { img: img1, title: "Consent Management Platform", rows: 4, cols: 8 },
    { img: img2, title: "Ensure Compliance", rows: 4, cols: 4 },
    { img: img3, title: "Data Privacy Cloud", rows: 2, cols: 5 },
    { img: img4, title: "Third-party Cookies", rows: 4, cols: 7 },
    { img: img5, title: "Customizable Cookie Banner", rows: 4, cols: 5 },
    { img: img6, title: "Free Website Privacy Scan", rows: 2, cols: 7 },
    { img: img7, title: "Powerful Analytics", rows: 4, cols: 6 },
    { img: img8, title: "Easy Integration", rows: 4, cols: 6 },
    { img: img9, title: "Enter URL", rows: 3, cols: 4 },
    { img: img10, title: "Audit Report", rows: 3, cols: 4 },
    { img: img11, title: "Partner Subscription", rows: 3, cols: 4 },
    { img: img12, title: "Global Compliance", rows: 3, cols: 5 },
    { img: img13, title: "For Businesses & Startups", rows: 3, cols: 7 },
  ];

  const IllustrationContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100vh",
    overflow: "scroll", // Hide overflowing content
    padding: theme.spacing(2),
    backgroundColor: "#F6F6F6",
  }));

  // Wrapper for infinite scrolling effect
  const ScrollWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    animation: "scrollUp 120s linear infinite",
    height: "200%",
    "@keyframes scrollUp": {
      "0%": { transform: "translateY(0%)" },
      "100%": { transform: "translateY(-100%)" },
    },
  }));

  const StyledImageListItem = styled(ImageListItem)(({ theme }) => ({
    overflow: "hidden",
    borderRadius: 12,
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      borderRadius: 12,
    },
  }));

  const renderImageGrid = () => {
    return (
      <IllustrationContainer>
        <ScrollWrapper>
          <ImageList
            sx={{
              width: "100%",
              height: "auto",
              overflow: "visible",
              mt: 0,
            }}
            variant="quilted"
            cols={12}
            gap={12}
          >
            {/* Duplicate the images for seamless scrolling */}
            {[...imageItems, ...imageItems].map((item, index) => (
              <StyledImageListItem
                key={index}
                cols={item.cols}
                rows={item.rows}
              >
                <img src={item.img} alt={item.title} loading="lazy" />
              </StyledImageListItem>
            ))}
          </ImageList>
        </ScrollWrapper>
      </IllustrationContainer>
    );
  };

  const illustration = useMemo(
    () => renderImageGrid(),
    //eslint-disable-next-line
    []
  );

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    if (isAuthenticated === true) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        if (!value) {
          return "Email is required";
        }
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          return "Invalid email address";
        }
        return "";

      case "password":
        if (!value) {
          return "Password is required";
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (touched[name]) {
      const fieldError = validateField(name, fieldValue);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const fieldError = validateField(name, fieldValue);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { email, password } = formData;

      if (!email || !password) {
        setError("Please fill in all required fields.");
        return;
      }

      const signInResponse = await signIn({ username: email, password });

      if (
        signInResponse.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setIsForgotPasswordDisabled(true);
        navigate("/change-password", { state: { email } });
        return;
      }

      const data = await getCurrentUser();
      const currentUser = await fetchUserAttributes();

      const organizationId = currentUser?.profile;

      if (organizationId !== undefined && organizationId !== null) {
        const numericId = Number(organizationId);

        if (!isNaN(numericId)) {
          localStorage.setItem("organization_id_str", String(numericId));
        }
      }

      if (data?.userId) {
        localStorage.setItem("user_id", data.userId);
      }

      // Wait for organization_id to be stored in local storage
      await waitForOrganizationId();
      const licenseResponse = await validateLicense();
      if (
        !licenseResponse ||
        licenseResponse?.data?.subscription?.status !== "active"
      ) {
        handleLogout(navigate);
        setLoading(false);
        showErrorToast(licenseResponse?.message);
        return;
      }
      // Check for license expiry
      const endDate = licenseResponse?.data?.subscription?.end_date;
      if (endDate && new Date(endDate) < new Date()) {
        handleLogout(navigate);
        setLoading(false);
        showErrorToast("Your subscription plan has been Expired. Please contact support!");
        return;
      }
      dispatch(setLicenseData(licenseResponse?.data));
      const response = await new Promise((resolve) =>
        setTimeout(async () => {
          const apiResponse = await getAllWebsites();
          resolve(apiResponse);
        }, 1000)
      );

      // Navigate based on response
      if (response?.status_code === 404) {
        navigate("/add-website");
      } else {
        showSuccessToast("Logged in successfully!");
        navigate("/dashboard");
      }
    } catch (error) {
      handleSignInError(error);
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to wait for organization_id
  const waitForOrganizationId = async () => {
    return new Promise((resolve) => {
      const checkStorage = () => {
        const storedOrgId = localStorage.getItem("organization_id_str");
        if (storedOrgId && !isNaN(Number(storedOrgId))) {
          resolve();
        } else {
          setTimeout(checkStorage, 100); // Retry after 100ms
        }
      };
      checkStorage();
    });
  };

  // Error handler function
  const handleSignInError = (error) => {
    if (error.name === "NotAuthorizedException") {
      setError("Incorrect username or password.");
    } else if (error.name === "UserNotConfirmedException") {
      setError("Please verify your email before logging in.");
    } else if (error.name === "UserNotFoundException") {
      setError("User does not exist.");
    } else if (axios.isCancel(error)) {
      setError("Request canceled. Please try again.");
    } else {
      setError("An error occurred during sign in. Please try again.");
    }
  };

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
      }}
    >
      {/* Left side - Image Grid Layout */}
      <Grid
        item
        md={6}
        lg={6}
        sx={{
          display: { xs: "none", md: "block" },
          background: "#F6F6F6",
          overflow: "auto",
        }}
      >
        {illustration}
      </Grid>

      {/* Right side - Form */}
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // p: 4,
          background: "#F6F6F6",
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#F8F8F8",
            padding: 3,
          }}
        >
          {/* Form Section */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {/* Logo at the top */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 4,
              }}
            >
              <img src={myIcon} alt="logo" width="250" height="50" />
            </Box>
            <Box
              sx={{
                width: "100%",
                maxWidth: 450,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Text above the form */}
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: "#5F5F5F",
                  fontWeight: 600,
                  fontFamily: "DM Sans",
                }}
              >
                Sign in to your account
              </Typography>

              <Paper
                elevation={2}
                sx={{
                  width: "90%",
                  p: 4,
                  borderRadius: "12px",
                  backgroundColor: "white",
                  border: "none",
                }}
                variant="outlined"
              >
                <Box component="form" onSubmit={handleSubmit}>
                  <InputLabel shrink htmlFor="email" required>
                    User name or Email
                  </InputLabel>
                  <TextField
                    margin="none"
                    size="small"
                    required
                    fullWidth
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                  />
                  <InputLabel shrink htmlFor="password" required>
                    Password
                  </InputLabel>
                  <TextField
                    margin="none"
                    size="small"
                    required
                    fullWidth
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end", // Align to the right
                    }}
                  >
                    <Link
                      href="/forgot-password"
                      sx={{
                        color: "#00B894",
                        textDecoration: "none",
                        fontWeight: 500,
                        fontSize: "16px",
                      }}
                      disabled={isForgotPasswordDisabled}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    size="small"
                    fullWidth
                    variant="outlined"
                    sx={{
                      mt: 2,
                      mb: 2,
                      "&:hover": {
                        backgroundColor: "#009B7D",
                        color: "white",
                      },
                      borderRadius: 1.5,
                      border: "2px solid #009B7D",
                      textTransform: "none",
                      fontSize: "1rem",
                      color: "#525455",
                      fontWeight: 700,
                    }}
                    disabled={loading}
                  >
                    {!loading ? "Sign In" : <CircularProgress size={20} />}
                  </Button>
                  <Typography color="error">{error}</Typography>
                </Box>
                <Link
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/sign-up")}
                >
                  Sign Up
                </Link>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
